<script>
import { debounce } from "lodash";
import Grid from "@/components/Grid";
import TextInput from "@/components/TextInput";
import Pagination from "@/components/Pagination";
import ErrorMessage from "@/components/ErrorMessage";
import { useResourcesStore } from "../stores/resources";
import { useI18n } from "vue-i18n";
// Style used for the printable table
const printTableStyle = `
html, body, table {
  margin: 0;
  padding: 0;
  border: 0;
  width: 100%;
  font-family: Helvetica, Arial, sans-serif;
}

table, th, td {
  color: #626262;
  border: 1px solid #212121;
  border-collapse: collapse;
  padding: 5px;
}
th {
  text-align: left;
}

tr:nth-child(odd) {
  background-color: white;
}
tr:nth-child(odd) {
  background-color: #f3f3f3;
}
`;

const statusCodes = {
  422: "components.town_selection.422",
  401: "views.modals.logout.401",
};

const sanitize = (str) => {
  const { t } = useI18n();
  const d = document.createElement("div");
  d.textContent = t(str);
  return d.innerHTML;
};
export default {
  setup() {
    const { t } = useI18n();
    const resourcesStore = useResourcesStore();
    return {
      resourcesStore,
      t,
    }
  },
  components: {
    Grid,
    TextInput,
    Pagination,
    ErrorMessage,
  },
  props: {
    buttons: {
      type: Array,
      default() {
        return [];
      },
    },
    buttonLoading: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      errors: [],
      loading: false,
    };
  },
  computed: {
    searchText() {
      return this.t("components.grid.search")
    },
    resources() {
      return this.resourcesStore.$state[this.type];
    },
    page() {
      return this.resources.page;
    },
    cols() {
      return this.resources.cols;
    },
    sort() {
      return this.resources.sort;
    },
    filter: {
      get() {
        return this.resources.filter || "";
      },
      set: debounce(function (filter) {
        const resourcesStore = useResourcesStore();
        resourcesStore.filter({ type: this.type, filter });
      }, 600),
    },
    filterFields: {
      get() {
        return this.resources.filterFields;
      },
      set(filterFields) {
        this.resourcesStore.filterFields({
          type: this.type,
          filterFields,
        })
      },
    },

    perPage: {
      get() {
        return this.resources.elements.perPage;
      },
      set: debounce(async function (perPage) {
        const resourcesStore = useResourcesStore();
        const error = await resourcesStore.perPage({
          type: this.type,
          perPage,
        });
        if (error) {
          const {
            status,
            statusText,
            data: { message },
          } = error;
          this.errors.push({
            id: Date.now(),
            message: statusCodes[status] || message || statusText,
          });
        }
      }, 600),
    },
    elements() {
      return this.resources.elements;
    },
    rows() {
      return this.resourcesStore.gridFormatted(this.type);
    },
  },
  async mounted() {
    if (this.rows.length === 0) {
      this.loading = true;
    }
    const error = await this.resourcesStore.refresh({ type: this.type });
    if (error) {
      this.errors.push({
        id: Date.now(),
        message: statusCodes[error.status] || error.data,
      });
    }
    this.loading = false;
  },
  methods: {
    closeMessage(id) {
      Object.keys(this.errors).forEach((key) => {
        if (id === this.errors[key].id) {
          this.errors.splice(key, 1);
        }
      });
    },
    async setPage({ page } = {}) {
      const error = await this.resourcesStore.page({ type: this.type, page });
      if (error) {
        this.errors.push({
          id: Date.now(),
          message: statusCodes[error.status] || error.data.message,
        });
      }
    },

    changeSort(index) {
      this.resourcesStore.sort({ type: this.type, index });
    },

    print() {
      // This is messy but it's optimized considering we want this to go fast for thousands of rows.
      const headers = `<th>${this.cols
        .map((col) => this.t(col.label || col.id))
        .join("</th><th>")}</th>`;
      const rows = this.rows
        .map(
          (row) =>
            `<tr><td>${row.values
              .map((val) => sanitize(val))
              .join("</td><td>")}</td></tr>`
        )
        .join("");
      const body = `<table><thead>${headers}</thead><tbody>${rows}</tbody></table>`;
      const DOM = `<html><head><style>${printTableStyle}</style></head><body>${body}</body></html>`;
      const newWin = window.open("");
      newWin.document.write(DOM);
      newWin.print();
      newWin.close();
    },
  },
};
</script>

<template>
  <div class="overflow-auto bg-white border-t border-solid border-gray-300">
    <ErrorMessage v-for="msg in errors" :key="msg.id" :value="msg" @input="closeMessage" />
    <div class="flex flew-row items-center py-2">
      <div class="mr-2 flex-1">
        <TextInput id="searchInput" v-model="filter" label="" data-cy="resourceSearch" :placeholder="searchText" />
      </div>
      <div class="pr-2">
        <button class="bg-white border-solid border border-gray-300 gray-600 text-lightGrey px-4 py-3" @click="print">
          {{ $t("components.generic_resources_views.print") }}
        </button>
      </div>
    </div>
    <div class="flex flex-row flex-wrap justify-start text-gray-600 mb-2">
      <span class="mr-1">{{
        $t("components.generic_resources_views.search")
      }}</span>
      <div v-for="col in cols.filter((col) => col.filter !== false)" :key="col.id"
        class="flex items-center select-none mx-1 cursor-pointer">
        <input :id="`fltr-${col.id}`" v-model="filterFields" type="checkbox" :value="col.filter || col.id" />
        <label :for="`fltr-${col.id}`" class="ml-1">{{
          $t(col.label) || col.id
        }}</label>
      </div>
    </div>

    <div class="text-gray-700 text-sm mt-4">
      <input v-model="perPage" type="number" class="w-8 border border-gray-300 p-1" /><span>{{
        $t("components.generic_resources_views.page_entries") }}</span>
    </div>
    <Grid ref="table" class="mt-2 mb-2" :loading="loading" :rows="rows" :columns="cols" :buttons="buttons" :sort="sort"
      @sort="changeSort" :buttonLoading="buttonLoading" />
    <span v-if="elements.from !== null &&
      elements.to !== null &&
      elements.total !== null
      " class="text-gray-700 text-sm">{{
    $t("components.generic_resources_views.show_entries", {
      from: elements.from,
      to: elements.to,
      total: elements.total,
    })
  }}</span>
    <div class="mt-4 w-full flex flex-row-reverse justify-center">
      <Pagination v-if="page.current !== null && page.last !== null" :current="page.current" :maximum="page.last"
        @goto="setPage" />
    </div>
  </div>
</template>


<style scoped>
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}
</style>
